<template>
  <v-card class="pa-5 w-100 h-100 d-flex flex-column">
    <div class="d-flex justify-space-between align-center mb-4">
      <div class="titre1">{{ widget.title }}</div>

      <v-select
          v-if="activeTabHasYearSelector"
          v-model="activeTabSelectedYear"
          :items="activeTabAvailableYears"
          density="compact"
          variant="outlined"
          class="year-select"
          style="max-width: 120px;"
          @update:modelValue="onSelectedChange"
      />
    </div>

    <div class="tab-bar-container">
      <div class="tabs">
        <div
            v-for="(tab, i) in tabs"
            :key="i"
            class="tab"
            :class="{ active: activeTabIndex == i }"
            @click="activeTabIndex = i"
        >
          {{ tab.title }}
        </div>
      </div>
    </div>

    <div class="flex-grow-1 mt-6">
      <component
          :is="resolvedComponent"
          v-bind="resolvedProps"
          v-on="events"
      />
    </div>
  </v-card>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import {DashboardWidgetWidgetTypes, WidgetInfo} from "@/generated/classes/DashboardWidget.ts";
import KpiWidget from "@/framework/dashboard/components/KpiWidget.vue";
import DoughnutChart from "@/framework/chart/components/DoughnutChart2.vue";
import BarChart from "@/framework/chart/components/BarChart2.vue";

export default defineComponent({
  name: 'MultiTabWidget',

  components: {
    KpiWidget,
    DoughnutChart,
    BarChart,
  },

  props: {
    widget: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      activeTabIndex: 0,
      selectedYear: null
    }
  },
  mounted() {
    this.selectedYear = this.widget.widgetMeta?.selectedOption ?? null
  },
  computed: {
    tabs() {
      return this.widget.widgetMeta?.tabs || []
    },
    activeTab() {
      return this.tabs[this.activeTabIndex] || null
    },

    resolvedComponent() {
      const type = this.activeTab?.widgetType;

      switch (type) {
        case DashboardWidgetWidgetTypes.KPI:
          return KpiWidget

        case DashboardWidgetWidgetTypes.DOUGHNUT:
          return DoughnutChart

        case DashboardWidgetWidgetTypes.BAR:
          return BarChart

        default:
          return null
      }
    },

    resolvedProps() {
      const w = this.activeTab;

      if (!w) return {} as any

      switch (w.widgetType) {
        case DashboardWidgetWidgetTypes.KPI:
          return {
            id: w.id,
            title: w.title,
            value: w.data?.value,
            btnLabel: w.widgetMeta?.buttonLabel
          }

        case DashboardWidgetWidgetTypes.DOUGHNUT:
          return {
            id: w.id,
            title: w.title,
            labels: w.data?.labels || [],
            data: w.data?.values || [],
            chartOptions: w.widgetMeta?.chartOptions || {}
          }

        case DashboardWidgetWidgetTypes.BAR:
          return {
            id: w.id,
            title: w.title,
            labels: Array.isArray(w.data?.labels) ? [...w.data.labels] : [],
            data: Array.isArray(w.data?.values) ? [...w.data.values] : [],
            chartOptions: w.widgetMeta?.chartOptions || {}
          }

        default:
          return {} as any
      }
    },

    events(): Record<string, () => void> {
      const event: any = {};
      event["widgetAction"] = (widgetInfo: WidgetInfo) => this.widgetAction(widgetInfo);

      return event;
    },

    activeTabHasYearSelector() {
      return this.activeTab?.widgetMeta?.dropdownOptions?.length > 0
    },

    activeTabAvailableYears() {
      return this.activeTab?.widgetMeta?.dropdownOptions || []
    },

    activeTabSelectedYear: {
      get() {
        return this.activeTab?.widgetMeta?.selectedOption ?? null
      },
      set(val: any) {
        if (this.activeTab?.widgetMeta) {
          this.activeTab.widgetMeta.selectedOption = val
        }
      }
    }
  },
  methods: {
    widgetAction(widgetInfo: WidgetInfo) {
      widgetInfo.selected = this.activeTabSelectedYear;
      widgetInfo.tab = this.activeTab.title;
      this.$emit("widgetAction", widgetInfo);
    },
    onSelectedChange(val) {
      const widgetInfo = new WidgetInfo();
      widgetInfo.id = this.activeTab.id;
      widgetInfo.label = this.activeTab.title;
      widgetInfo.tab = this.activeTab.title;
      widgetInfo.selected = this.activeTabSelectedYear;

      this.$emit("select", widgetInfo);
    },

  }

})
</script>

<style scoped>
.tab-bar-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  border-bottom: 1px solid #ddd;
  padding-bottom: 0;
}

.tabs {
  display: flex;
  gap: 8px;
}

.tab {
  position: relative;
  padding: 6px 14px;
  cursor: pointer;
  font-weight: 500;
  color: #555;
  background: transparent;
  border: 1px solid transparent;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  transition: 0.2s;
}

.tab.active {
  color: #d32f2f;
  background: white;
  border-color: #ddd;
  border-bottom-color: white;
  z-index: 2;
}

.tab.active::after {
  content: "";
  position: absolute;
  bottom: -1px;
  left: 12px;
  right: 12px;
  height: 2px;
  background-color: #d32f2f;
  border-radius: 2px;
}

.tab:not(.active):hover {
  background: rgba(0, 0, 0, 0.04);
  border-color: #eee;
}

.year-select .v-field {
  border-color: #d32f2f !important;
}

.year-select .v-field:hover {
  border-color: #b71c1c !important;
}

.year-select .v-field--focused {
  border-color: #d32f2f !important;
}
</style>
